import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import MaterialInput from '../form/MaterialInput';
import { GradientTextAction, InputLabel, Poppins, Spacer, StyledAvatar } from 'src/common';
import { useAuth } from 'src/state/auth';
import Button from '../form/Button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import { getProfileCredentials } from 'src/utils/auth';
import { UserOptionType } from 'src/api/types';
import { UserListItem } from './comps';
import { InputAdornment, Menu } from '@mui/material';
import { useCache } from 'src/graphql/useCache';
import { useInviteUsersMutation } from 'src/graphql/_generated';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const Div = styled.div`
  position: relative;
`;

const Input = styled(MaterialInput)<{ $isRealUserSelected?: boolean }>`
  input {
    background-color: ${({ theme }) => theme.colors.cultured};
    ${({ $isRealUserSelected }) =>
      $isRealUserSelected &&
      css`
        padding-left: 10px !important;
      `}
  }

  ${({ $isRealUserSelected, theme }) =>
    $isRealUserSelected &&
    css`
      .MuiInputBase-root {
        background-color: ${theme.colors.cultured};
      }
      .MuiInputAdornment-root {
        background-color: transparent;
      }
    `}
`;

const Paddings = styled.div`
  padding: 0 19px;
`;

const StyledMenu = styled(Menu)<{ $isInviteOpen: boolean; $menuWidth?: number }>`
  .MuiList-padding {
    padding: 0 !important;
  }
  & .MuiPaper-root {
    border-radius: 3px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
    min-width: 0 !important;
    max-width: none !important;
    width: ${({ $menuWidth }) => ($menuWidth ? `${$menuWidth}px !important` : 'auto')};
  }

  .us-container {
    width: 100%;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 3px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};

    &__list {
      margin: 0 10px 0 10px;
      max-height: ${({ $isInviteOpen }) => ($isInviteOpen ? 200 : 360)}px;
      overflow-y: auto;
      overflow-x: hidden;
      display: grid;
    }

    &__btns {
      display: flex;
      flex-wrap: wrap;
      grid-gap: 10px;
      button {
        flex: 1;
      }
    }

    &__err {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__divider {
      height: 1px;
      background-color: ${({ theme }) => theme.colors.stroke};
    }
  }
`;

export const validationSchema = Yup.object({
  text: Yup.string().required('Required').max(100, 'Max 100 characters'),
  email: Yup.string().email('Invalid email').required('Required').max(100, 'Max 100 characters'),
});

interface UserInputProps {
  placeholder?: string;
  onBlur?: () => void;
  value: UserOptionType;
  onChange: (e: UserOptionType) => void;
  error?: boolean;
  helperText?: string | false;
  onSelectUser?: (user: UserOptionType) => void;
  disabled?: boolean;
}

const UserInput: React.FC<UserInputProps> = ({
  placeholder,
  onBlur,
  value,
  onChange,
  error,
  helperText,
  onSelectUser,
  disabled,
}) => {
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const { profile } = useAuth();
  const [users, setUsers] = useState<UserOptionType[]>([]);
  const { usersOptios } = useAuth();
  const { refetchQuery } = useCache();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const shouldTriggerBlur = useRef<boolean>(true);

  const canInvite = profile?.activeWorkspace?.actionsOnThisWorkspace?.canInviteUsers;

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsInviteOpen(false);
    if (onBlur && shouldTriggerBlur.current) {
      onBlur();
    }
    shouldTriggerBlur.current = true;
  };

  useEffect(() => {
    if (!anchorEl) return;

    const isWithinBounds = (el: HTMLElement | null, x: number, y: number) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (menuContentRef.current?.contains(target)) return;
      // MUI portal sits above input in z-index — event.target is Modal root, not input.
      if (isWithinBounds(ref.current, e.clientX, e.clientY)) {
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      if (isWithinBounds(menuContentRef.current, e.clientX, e.clientY)) return;
      handleMenuClose();
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const normalizedSearch = value.text?.toLowerCase() || '';

    const filtered = _.chain(usersOptios)
      .filter((user) =>
        _.some(
          user,
          (value, key) => (key === 'text' || key === 'email') && _.includes(value?.toLowerCase(), normalizedSearch),
        ),
      )
      .orderBy([(el) => el.text?.toLowerCase()], 'asc')
      .value();

    setUsers(filtered);
  }, [usersOptios, value]);

  const [invite, { error: inviteErr }] = useInviteUsersMutation({
    onCompleted: () => {
      shouldTriggerBlur.current = false; // Prevent onBlur when user completes invite
      onSelectUser && onSelectUser({ text: values.text, email: values.email });
      refetchQuery({ include: ['profile'] });
      setIsInviteOpen(false);
      resetForm();
      setAnchorEl(null);
      showSuccessToast(toastMessages.user.invited);
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    resetForm,
    isValid,
    isSubmitting,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    validationSchema,
    initialValues: {
      text: '',
      email: '',
    },
    onSubmit: (values) =>
      invite({
        variables: {
          workspaceId: profile?.activeWorkspace?.id as string,
          invitesData: [
            {
              email: values.email,
              name: values.text,
            },
          ],
        },
      }),
  });

  const handleOpenInviteForm = (str: string) => {
    Yup.string()
      .email('Invalid email')
      .required('Required')
      .validate(str)
      .then(() => {
        setFieldValue('email', str);
        setFieldValue('text', '');
      })
      .catch(() => {
        setFieldValue('text', str);
        setFieldValue('email', '');
      })
      .finally(() => setIsInviteOpen(true));
  };

  const handleSelectUser = (user: UserOptionType) => {
    shouldTriggerBlur.current = false;
    onSelectUser && onSelectUser(user);
    resetForm();
    setAnchorEl(null);
    setIsInviteOpen(false);
  };

  useEffect(() => {
    const user = users.find((el) => el.email && el.email === value.email);

    if (user && value.email) {
      const isRealUserSelected = user?.text === value.text;
      if (!isRealUserSelected && !_.isEqual(value, { text: value.text })) {
        onChange({ text: value.text });
      }
    }
  }, [users, value]);

  const isPlaceholder = Boolean((!value.inviteId && !value.memberId) || !value.text);

  return (
    <Div ref={ref}>
      <Input
        inputRef={inputRef}
        value={value.text}
        onChange={(e) => onChange({ ...value, text: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleMenuClose();
            (e.target as HTMLElement).blur();
          }
        }}
        onBlur={() => {
          if (!anchorEl) {
            onBlur && onBlur();
          }
          resetForm();
        }}
        onFocus={(e) => {
          if (!anchorEl) {
            setAnchorEl(e.currentTarget);
            setMenuWidth(e.currentTarget.offsetWidth);
          }
        }}
        onClick={(e) => {
          if (!anchorEl) {
            setAnchorEl(e.currentTarget);
            setMenuWidth(e.currentTarget.offsetWidth);
          }
          e.stopPropagation();
        }}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        fullWidth
        autoComplete="off"
        disabled={disabled}
        slotProps={{
          input: {
            startAdornment: !isPlaceholder ? (
              <InputAdornment position="end">
                <StyledAvatar $xsmall src={value.profilePhotoPath || ''}>
                  {getProfileCredentials(value.text || '')}
                </StyledAvatar>
              </InputAdornment>
            ) : undefined,
          },
        }}
        data-cy="owner-input"
        $isRealUserSelected={!isPlaceholder}
      />

      <StyledMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown') handleMenuClose();
        }}
        $isInviteOpen={isInviteOpen}
        $menuWidth={menuWidth}
        TransitionComponent={undefined}
        BackdropProps={{ style: { pointerEvents: 'none' } }}
      >
        <div className="us-container" ref={menuContentRef} onClick={(e) => e.stopPropagation()}>
          {!users.length && (
            <>
              <Spacer $px={20} />
              <Paddings>
                <Poppins px={14} color="davysGrey">
                  {canInvite
                    ? 'User not found. You can either keep the free text or invite the user by email.'
                    : 'User not found. You can keep the free text.'}
                </Poppins>
              </Paddings>
            </>
          )}

          {users.length > 0 && (
            <>
              <Spacer $px={20} />
              <div className="us-container__list styled-scroll styled-scroll--themed">
                {users.map((el) => (
                  <UserListItem
                    key={el.id}
                    user={el}
                    onClick={onSelectUser ? () => handleSelectUser(el) : undefined}
                    onInviteClick={() => handleOpenInviteForm(el.text || '')}
                  />
                ))}
              </div>
            </>
          )}
          {canInvite && (
            <>
              <Spacer $px={20} />
              <div className="us-container__divider" />
              <Spacer $px={15} />
              <Paddings>
                {isInviteOpen ? (
                  <>
                    <Poppins px={16} weight={600} color="cflowerBlue">
                      Invite User
                    </Poppins>
                    <Spacer $px={20} />
                    <InputLabel>Name</InputLabel>
                    <MaterialInput
                      name="text"
                      placeholder="Name"
                      value={values.text}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.text && !!errors.text}
                      helperText={touched.text && errors.text}
                      fullWidth
                    />
                    <Spacer $px={17} />
                    <InputLabel>Email</InputLabel>
                    <MaterialInput
                      name="email"
                      placeholder="Email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      fullWidth
                    />
                    <Spacer $px={30} />
                    <div className="us-container__btns">
                      <Button
                        secondary
                        onClick={() => {
                          setIsInviteOpen(false);
                          resetForm();
                        }}
                      >
                        CANCEL
                      </Button>
                      <Button primary disabled={!isValid || isSubmitting} loading={isSubmitting} onClick={() => handleSubmit()}>
                        INVITE
                      </Button>
                    </div>
                    {inviteErr && (
                      <>
                        <Spacer $px={10} />
                        <div className="us-container__err">
                          <Poppins color="error" px={14}>
                            {inviteErr.message || 'Something went wrong'}
                          </Poppins>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <GradientTextAction px={16} weight={500} onClick={() => handleOpenInviteForm(value.text || '')}>
                    + Invite user
                  </GradientTextAction>
                )}
              </Paddings>
            </>
          )}
          <Spacer $px={18} />
        </div>
      </StyledMenu>
    </Div>
  );
};

export default UserInput;
